/**
 * The transaction designer's editor for the Export system action
 * (`{"handler": "system", "system_op": "export", "export": {…}}`).
 *
 * What people may choose — where Export shows, the formats, the row cap,
 * "As on screen", whether organizations make their own templates, "ask why"
 * — and the STANDARD templates shipped with the app, edited in the same
 * TemplateEditor an organization's functional admin uses (drag-and-drop
 * columns, layouts, the engine preview once the model is saved).
 * Doc: docs/plans/transaction-data-export.md.
 */
import { useMemo, useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import {
  TemplateEditor, allSqlModels, blankTemplate, columnFormKey,
  type ExportCatalogForm, type ExportFormat, type ExportTemplate, type TransactionModel,
} from '@twasta/page-runtime/txn'
import type { ActionDef, ExportActionSettings as ExportCfg } from '@twasta/page-runtime/txn'
import { platformExportTransport } from '@/api/txnExport'

const FORMATS: Array<{ id: ExportFormat; label: string }> = [
  { id: 'xlsx', label: 'Excel (.xlsx)' }, { id: 'csv', label: 'CSV' }, { id: 'json', label: 'JSON' },
]
const PLACES: Array<{ id: 'list' | 'bulk' | 'record'; label: string }> = [
  { id: 'list', label: 'List toolbar' }, { id: 'bulk', label: 'Selected records bar' },
  { id: 'record', label: 'Open record ⋮' },
]
const CODED = new Set(['ddlb', 'radio', 'checkbox', 'bool_check'])

function truthy(v: unknown): boolean {
  return v === true || String(v ?? '').trim().toLowerCase() === 'true'
}

function kindOf(colType: string): string {
  const t = (colType || '').toUpperCase()
  if (/^(NUMBER|NUMERIC|DECIMAL|INTEGER|INT|BIGINT|SMALLINT|DOUBLE|FLOAT|REAL)/.test(t)) return 'number'
  if (t === 'DATE') return 'date'
  if (/^(DATETIME|TIMESTAMP)/.test(t)) return 'datetime'
  if (t === 'BOOLEAN') return 'boolean'
  return 'text'
}

/** The catalog from the model being designed (the engine's own catalog is
 *  used everywhere else; this mirrors it for the unsaved model). */
function designCatalog(model: TransactionModel): ExportCatalogForm[] {
  const forms = allSqlModels(model)
  if (!forms.length) return []
  const headerNo = forms[0].formNo
  return forms
    .filter((f, i) => i === 0 || !f.sm.parent_form_no || f.sm.parent_form_no === headerNo
      || f.sm.parent_form_no === f.formNo)
    .map((f, i) => ({
      form_no: f.formNo, label: f.sm.form_title || `Form ${f.formNo}`, header: i === 0,
      columns: (f.sm.columns || []).map(w => w?.column).filter(Boolean).map(c => {
        const kind = kindOf(String(c!.col_type || ''))
        const mask = String((c as { edit_mask?: { mask?: string } }).edit_mask?.mask || '').toLowerCase()
        return {
          key: columnFormKey(c!), label: c!.heading || c!.name, list_label: c!.list_heading || c!.heading || c!.name,
          kind, coded: CODED.has(mask) || kind === 'boolean', person: false, pk: truthy(c!.key),
          secure: !!(c as { secure?: unknown }).secure, uda: false, hidden: truthy(c!.hidden),
        }
      }),
    }))
}

export function ExportActionSettings({ def, onPatch, model, project }: {
  def: ActionDef
  onPatch: (patch: Partial<ActionDef>) => void
  model?: TransactionModel
  project?: string
}) {
  const cfg: ExportCfg = def.export || {}
  const set = (p: Partial<ExportCfg>) => onPatch({ export: { ...cfg, ...p } })
  const formats = cfg.formats?.length ? cfg.formats : FORMATS.map(f => f.id)
  const places = cfg.places?.length ? cfg.places : PLACES.map(p => p.id)
  const templates = cfg.templates || []
  const catalog = useMemo(() => (model ? designCatalog(model) : []), [model])
  const [editing, setEditing] = useState<{ index: number; draft: ExportTemplate } | null>(null)
  const transport = useMemo(() => (project ? platformExportTransport(project) : null), [project])
  const objName = model?.obj_name || ''

  const toggle = <T extends string>(list: T[], v: T): T[] =>
    list.includes(v) ? list.filter(x => x !== v) : [...list, v]

  const saveDraft = () => {
    if (!editing) return
    const next = templates.slice()
    const clean = { ...editing.draft }
    delete (clean as Partial<ExportTemplate>).scope
    if (editing.index < 0) next.push(clean)
    else next[editing.index] = clean
    if (clean.is_default) next.forEach((t, j) => { if (t.id !== clean.id) next[j] = { ...t, is_default: false } })
    set({ templates: next })
    setEditing(null)
  }

  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 6 }} data-tw-group="export-action-settings">
      <div style={note}>
        This screen's <strong>Export</strong>. It was added when the transaction was created, like Add and Edit;
        delete or disable this action to turn Export off everywhere. Who may export is the <strong>Export</strong> right
        on this screen (it follows the view right until a role is granted it); organizations' own templates are
        managed by their functional admin (<strong>Manage export templates</strong>).
      </div>
      <div>
        <div style={lbl}>Shown on</div>
        <div style={chips}>
          {PLACES.map(p => (
            <button key={p.id} type="button" style={chip(places.includes(p.id))}
              onClick={() => set({ places: toggle(places, p.id) })}>{p.label}</button>
          ))}
        </div>
      </div>
      <div>
        <div style={lbl}>File formats people may choose</div>
        <div style={chips}>
          {FORMATS.map(f => (
            <button key={f.id} type="button" style={chip(formats.includes(f.id))}
              onClick={() => { const n = toggle(formats, f.id); if (n.length) set({ formats: n }) }}>{f.label}</button>
          ))}
        </div>
      </div>
      <label style={{ display: 'grid', gap: 4 }}>
        <span style={lbl}>Most records in one export</span>
        <input type="number" min={0} style={inp} value={cfg.max_rows ?? ''} placeholder="The hosting plan's limit"
          onChange={e => set({ max_rows: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value) || 0) })} />
        <span style={hint}>Leave blank for the hosting plan's limit. A value above it is capped by the plan.</span>
      </label>
      <Check on={cfg.offer_list_view !== false} onChange={v => set({ offer_list_view: v })}
        title="Offer “As on screen”" hint="Export the columns of the person's own list, without a template." />
      <Check on={cfg.org_templates !== false} onChange={v => set({ org_templates: v })}
        title="Let organizations make their own templates" hint="Each organization's functional admin, for that organization only." />
      <Check on={!!cfg.require_reason} onChange={v => set({ require_reason: v })}
        title="Ask why before exporting" hint="The reason is kept with the export's audit entry." />

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={lbl}>Standard templates</span>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!catalog.length}
            onClick={() => setEditing({ index: -1, draft: { ...blankTemplate(catalog, model?.obj_name || 'Export'),
              id: `std-${Date.now().toString(36)}` } })}><Plus size={13} /> New</button>
        </div>
        <div style={hint}>Shipped with the app to every organization. An organization can copy one and change the copy.</div>
        {templates.map((t, j) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0',
            borderBottom: '1px solid var(--color-border)' }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <strong>{t.name}</strong>{t.is_default ? ' · default' : ''}
              <span style={{ ...hint, display: 'block' }}>
                {t.details.length ? (t.layout.mode === 'same_sheet' ? 'Lines on the same sheet' : 'Separate sheets') : 'Records only'}
                {' · '}{t.header.length + t.details.reduce((n, d) => n + d.columns.length, 0)} columns
              </span>
            </span>
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => setEditing({ index: j, draft: JSON.parse(JSON.stringify(t)) })}>Edit</button>
            <button type="button" className="btn btn-secondary btn-sm" aria-label={`Delete ${t.name}`}
              onClick={() => set({ templates: templates.filter((_, k) => k !== j) })}><Trash2 size={13} /></button>
          </div>
        ))}
        {!templates.length && <div style={{ ...hint, padding: '6px 0' }}>None yet.</div>}
      </div>

      {editing && (
        <div role="dialog" aria-modal="true" aria-label="Standard export template" style={overlay}>
          <div style={frame}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
              borderBottom: '1px solid var(--color-border)' }}>
              <strong style={{ flex: 1, fontSize: 16 }}>Standard export template</strong>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(null)} aria-label="Close"><X size={15} /></button>
            </div>
            <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
              <TemplateEditor template={editing.draft} catalog={catalog} formats={formats as ExportFormat[]}
                onChange={(t) => setEditing({ ...editing, draft: t })}
                roleHint="A role narrows who sees the template in every organization. It never gives Export to anyone."
                onPreview={transport && objName
                  ? (d) => transport.preview({ obj: objName, draft: d })
                  : undefined} />
              <div style={{ ...hint, marginTop: 8 }}>The preview reads the SAVED screen: save the transaction to preview new columns.</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px',
              borderTop: '1px solid var(--color-border)' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={!editing.draft.name.trim()}
                onClick={saveDraft}>Keep template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Check({ on, onChange, title, hint: h }: { on: boolean; onChange: (v: boolean) => void; title: string; hint: string }) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
      <input type="checkbox" checked={on} onChange={e => onChange(e.target.checked)}
        style={{ width: 16, height: 16, marginTop: 3, accentColor: 'var(--color-accent)' }} />
      <span>{title}<span style={{ ...hint, display: 'block' }}>{h}</span></span>
    </label>
  )
}

const lbl: React.CSSProperties = { fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)' }
const hint: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }
const note: React.CSSProperties = { ...hint, background: 'var(--color-surface-2)', borderRadius: 6, padding: '8px 10px' }
const inp: React.CSSProperties = {
  padding: '7px 10px', minHeight: 36, borderRadius: 6, border: '1px solid var(--color-border-strong)',
  background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: 14, maxWidth: 220,
}
const chips: React.CSSProperties = { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }
const chip = (on: boolean): React.CSSProperties => ({
  borderRadius: 999, padding: '4px 12px', minHeight: 30, fontSize: 13, cursor: 'pointer',
  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
  background: on ? 'var(--color-accent-weak)' : 'var(--color-input-bg)',
  color: 'var(--color-text)', fontWeight: on ? 600 : 400,
})
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 9700, background: 'rgba(15, 20, 28, 0.55)', display: 'flex',
  alignItems: 'stretch', justifyContent: 'center', padding: '24px 16px', overflow: 'auto',
}
const frame: React.CSSProperties = {
  width: '100%', maxWidth: 1280, margin: 'auto 0', background: 'var(--color-bg)', color: 'var(--color-text)',
  borderRadius: 12, display: 'flex', flexDirection: 'column', border: '1px solid var(--color-border)',
  boxShadow: '0 20px 60px rgba(0,0,0,.35)', maxHeight: 'calc(100vh - 48px)',
}
